import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import api from "@shared/api";

import { getPageViewTemplate } from "./pageViewTemplate";

// A client-side redirect (a bare `/admin` landing on its first section tab, OnboardingGuard sending
// `/procedures` to the onboarding screen) renders the pre-redirect location for one tick before
// replacing it -- delaying the send lets a same-tick redirect cancel it via the effect's own cleanup
// below, so only the location the person actually lands on is ever counted.
export const PAGE_VIEW_SEND_DELAY_MS = 1000;

/**
 * Sends one page view per screen a signed-in person settles on.
 *
 * Calls the api wrapper directly rather than dispatching a redux action -- this is telemetry, not a
 * domain read/write, and errorHandlerMiddleware (store.tsx) turns any action whose type contains
 * "FAILURE" into an error toast, which a background beacon failing must never trigger.
 *
 * @param signedIn Whether a session is active; nothing is sent while signed out.
 */
export const usePageViewTracking = (signedIn: boolean): void => {
  const location = useLocation();
  const lastSentTemplateRef = useRef<string | null>(null);

  useEffect(() => {
    if (!signedIn) {
      // A same-tab sign-out then sign-in back onto this same page must be counted as a fresh view,
      // not silently deduped against whatever the previous session last sent.
      lastSentTemplateRef.current = null;
      return undefined;
    }

    const template = getPageViewTemplate(location.pathname, location.search);

    const timeoutId = setTimeout(() => {
      if (template === lastSentTemplateRef.current) {
        return;
      }

      lastSentTemplateRef.current = template;
      api.trackPageView(template).catch(() => {
        // Swallowed: a rejection here must never surface, matching the reasoning above.
      });
    }, PAGE_VIEW_SEND_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [signedIn, location.pathname, location.search]);
};
